import React, { useEffect, useState } from "react";
import { IoMdEye } from "react-icons/io";
import { RiImageAddFill } from "react-icons/ri";
import { AiOutlineClose } from "react-icons/ai";
import { Link } from "react-router-dom";
import Pagination from "../../../../components/common/Pagination";
import {
  getAllOffers,
  addOfferForAll,
  getRestaurantOrHotels,
  updateOffer,
} from "../../../../api/owner/offers";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { CiEdit } from "react-icons/ci";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const PromotionList = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [formData, setFormData] = useState({
    restaurantId: "",
    title: "",
    description: "",
    discountPercentage: "",
    startDate: "",
    endDate: "",
    image: null,
    imagePreview: null,
  });
  const [errors, setErrors] = useState({
    restaurantId: "",
    title: "",
    description: "",
    discountPercentage: "",
    startDate: "",
    endDate: "",
    image: "",
  });
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["restaurant-offers", page],
    queryFn: () => getAllOffers({ page, limit, hotelId: null }),
    keepPreviousData: true,
  });

  const offers = data?.data?.offers || [];
  const pagination = data?.data?.pagination || {};

  const { data: restraurantsData } = useQuery({
    queryKey: ["hotels-restaurants", false],
    queryFn: () => getRestaurantOrHotels({ query: { restaurant: true } }),
    keepPreviousData: true,
  });

  const hotels = restraurantsData?.data?.restaurants;

  const { mutate: addOfferMutation , isPending:isAdding } = useMutation({
    mutationFn: addOfferForAll,
    onSuccess: () => {
      const restaurantPromotionList = t("restaurantPromotionList");
      const successMessage =
        restaurantPromotionList?.toastMessages?.addSuccess ||
        "Restaurant offer added successfully!";
      toast.success(successMessage, {
        duration: 4000,
        position: "top-center",
      });
      queryClient.invalidateQueries(["restaurant-offers"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("restaurantPromotionList")?.toastMessages?.addError ||
        t("restaurantPromotionList").error;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateOfferMutation , isPending: isUpdating } = useMutation({
    mutationFn: ({ offerId, data }) => {
      return updateOffer(offerId, data);
    },
    onSuccess: () => {
      const successMessage =
        t("restaurantPromotionList")?.toastMessages?.updateSuccess ||
        "Restaurant offer updated successfully!";
      toast.success(successMessage, {
        duration: 4000,
        position: "top-center",
      });
      queryClient.invalidateQueries(["restaurant-offers"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("restaurantPromotionList")?.toastMessages?.updateError ||
        t("restaurantPromotionList").error;
      toast.error(errorMessage);
    },
  });

  const closeModal = () => {
    setIsPopupOpen(false);
    setIsUpdateMode(false);
    setFormData({
      restaurantId: "",
      title: "",
      description: "",
      discountPercentage: "",
      startDate: "",
      endDate: "",
      image: null,
      imagePreview: null,
    });
    setErrors({
      restaurantId: "",
      title: "",
      description: "",
      discountPercentage: "",
      startDate: "",
      endDate: "",
      image: "",
    });
  };


  const validateForm = (data) => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (!data.restaurantId)
      newErrors.restaurantId = t(
        "restaurantPromotionList"
      ).modal.validation.restaurantRequired;

    if (!data.title)
      newErrors.title = t(
        "restaurantPromotionList"
      ).modal.validation.titleRequired;

    if (!data.description)
      newErrors.description = t(
        "restaurantPromotionList"
      ).modal.validation.descriptionRequired;

    if (!data.discountPercentage) {
      newErrors.discountPercentage = t(
        "restaurantPromotionList"
      ).modal.validation.discountRequired;
    } else {
      const discount = parseFloat(data.discountPercentage);
      if (isNaN(discount) || discount < 0 || discount > 100) {
        newErrors.discountPercentage = t(
          "restaurantPromotionList"
        ).modal.validation.discountRange;
      }
    }

    if (!data.startDate) {
      newErrors.startDate = t(
        "restaurantPromotionList"
      ).modal.validation.startDateRequired;
    } else if (startDate < today) {
      newErrors.startDate = t(
        "restaurantPromotionList"
      ).modal.validation.startDatePast;
    }

    if (!data.endDate) {
      newErrors.endDate = t(
        "restaurantPromotionList"
      ).modal.validation.endDateRequired;
    } else if (endDate < startDate) {
      newErrors.endDate = t(
        "restaurantPromotionList"
      ).modal.validation.endDateAfter;
    }

    // Image is required only when adding new offer, or when updating if no existing image
    if (!isUpdateMode && !data.image) {
      newErrors.image = t(
        "restaurantPromotionList"
      ).modal.validation.imageRequired;
    } else if (isUpdateMode && !data.image && !data.imagePreview) {
      newErrors.image = t(
        "restaurantPromotionList"
      ).modal.validation.imageRequired;
    }

    return newErrors;
  };

  const openModalForUpdate = (offer) => {
    setIsPopupOpen(true);
    setIsUpdateMode(true);
    setSelectedOffer(offer);
    const imageUrl = offer.image ? (offer.image.startsWith('http') ? offer.image : `${offer.image}`) : null;
    setFormData({
      restaurantId: offer.restaurantId?._id || "",
      title: offer.title,
      description: offer.description,
      discountPercentage: offer.discountPercentage,
      startDate: offer.startDate
        ? new Date(offer.startDate).toISOString().split("T")[0]
        : "",
      endDate: offer.endDate
        ? new Date(offer.endDate).toISOString().split("T")[0]
        : "",
      imagePreview: imageUrl,
      image: imageUrl, // Keep existing image URL for FormData
    });
  };

  const handleAddOffer = () => {
    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      // Skip image if it's a URL string (existing image) and we're updating
      // Only append if it's a File object (new upload) or if adding new offer
      if (key === 'image' && isUpdateMode && value && typeof value === 'string') {
        // Existing image URL - backend should handle this, but we'll append it
        payload.append(key, value);
      } else if (value !== null && value !== undefined && value !== '') {
        payload.append(key, value);
      }
    });

    if (isUpdateMode) {
      updateOfferMutation({ offerId: selectedOffer._id, data: payload });
    } else {
      addOfferMutation(payload);
    }
  };

  const validateField = (fieldName, value) => {
    const updatedFormData = { ...formData, [fieldName]: value };
    const newErrors = validateForm(updatedFormData);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: newErrors[fieldName] || "",
    }));
  };

  return (
    <>
      <div className="flex flex-wrap gap-[10px] justify-between items-center">
        <h2 className="md:text-[22px] text-lg font-semibold text-black">
          {t("restaurantPromotionList").title}
        </h2>
        <button
          onClick={() => setIsPopupOpen(true)}
          className="inline-block w-auto min-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[18px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
        >
          {t("restaurantPromotionList").createButton}
        </button>
      </div>

      <div className="lg:mt-[60px] mt-[20px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white">
          <h2 className="text-base font-semibold">
            {t("restaurantPromotionList").listTitle}
          </h2>

          {isPending ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[30px]">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.restaurantName}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.title}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.description}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.discount}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.duration}
                    </th>{" "}
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.status}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("restaurantPromotionList").table.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {offers?.map((offer) => {
                    const description = offer.description
                      ? offer.description.split(" ").slice(0, 5).join(" ") +
                        "..."
                      : "";
                    return (
                      <tr key={offer._id} className="border-t border-[#E4E4E4]">
                        <td>{offer.restaurantId?.name || "N/A"}</td>

                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {offer.title}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {description}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {offer.discountPercentage}%
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {new Date(offer.startDate).toLocaleDateString()} -{" "}
                          {new Date(offer.endDate).toLocaleDateString()}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          <div
                            className={`p-[8px] rounded-full max-w-[129px] text-center text-sm text-white ${
                              offer.status === "pending"
                                ? "bg-[#FF9D00]"
                                : offer.status === "approved"
                                ? "bg-green-600"
                                : "bg-red-500"
                            }`}
                          >
                            {offer.status.charAt(0).toUpperCase() +
                              offer.status.slice(1)}
                          </div>
                        </td>
                        <td className="py-[20px] px-[20px]">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/restaurantofferdetail/${offer._id}`}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                            >
                              <IoMdEye className="text-base text-black" />
                            </Link>

                            {offer?.status === "pending" && (
                              <button
                                onClick={() => openModalForUpdate(offer)}
                                className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                              >
                                <CiEdit className="text-base text-black" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {offers.length > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
        <div className="w-full rounded-[20px] sm:p-[30px] p-[15px]">
          <h2 className="text-[22px] font-medium mb-[19px]">
            {isUpdateMode
              ? t("restaurantPromotionList").modal.updateTitle
              : t("restaurantPromotionList").modal.addTitle}
          </h2>

          <div className="overflow-y-auto h-[350px]">
            <div className="grid sm:grid-cols-2 gap-[16px] mb-[16px]">
              <div className="">
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.restaurantName}
                </label>
                <select
                  value={formData.restaurantId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, restaurantId: value });
                    validateField("restaurantId", value);
                  }}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.restaurantId ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                >
                  <option value="">
                    {
                      t("restaurantPromotionList").modal.placeholder
                        .restaurantName
                    }
                  </option>
                  {hotels?.map((hotel) => (
                    <option key={hotel._id} value={hotel._id}>
                      {hotel.name}
                    </option>
                  ))}
                </select>
                {errors.restaurantId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.restaurantId}
                  </p>
                )}
              </div>

              <div className="">
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.title}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, title: value });
                    validateField("title", value);
                  }}
                  placeholder={
                    t("restaurantPromotionList").modal.placeholder.title
                  }
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.title ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-[16px] mb-[16px]">
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.startDate}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, startDate: value });
                    validateField("startDate", value);
                  }}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.startDate ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.startDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.startDate}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.endDate}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, endDate: value });
                    validateField("endDate", value);
                  }}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.endDate ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.endDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-[16px] mb-[16px]">
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.discount}
                </label>
                <input
                  type="number"
                  value={formData.discountPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, discountPercentage: value });
                    validateField("discountPercentage", value);
                  }}
                  placeholder={
                    t("restaurantPromotionList").modal.placeholder.discount
                  }
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.discountPercentage
                      ? "border-red-500"
                      : "border-[#DDDDDD]"
                  }`}
                />
                {errors.discountPercentage && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.discountPercentage}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("restaurantPromotionList").modal.description}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, description: value });
                    validateField("description", value);
                  }}
                  placeholder={
                    t("restaurantPromotionList").modal.placeholder.description
                  }
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none h-[52px] ${
                    errors.description ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.description}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white p-5 border-dashed border-2 rounded-[10px] text-center relative w-full max-w-[200px] h-[120px] mx-auto flex flex-col items-center justify-center cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const imagePreview = URL.createObjectURL(file);
                    setFormData({ ...formData, image: file, imagePreview });
                    validateField("image", file);
                  }
                }}
                className="opacity-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full cursor-pointer z-10"
              />
              {formData.imagePreview ? (
                <>
                  <img
                    src={formData.imagePreview}
                    alt="Selected"
                    className="w-full h-full object-cover rounded-[10px]"
                    onError={(e) => {
                      // If image fails to load, show placeholder
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full flex-col items-center justify-center bg-[#F5F5F5] rounded-[10px]">
                    <RiImageAddFill size={32} className="text-[#9E9E9E] mb-2" />
                    <p className="text-xs text-[#9E9E9E] font-medium">Image not available</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, image: null, imagePreview: null });
                      validateField("image", null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg z-20 flex items-center justify-center transition-colors"
                    aria-label="Remove selected image"
                    title="Remove image"
                  >
                    <AiOutlineClose size={18} />
                  </button>
                </>
              ) : (
                <>
                  <RiImageAddFill size={32} className="text-[#9E9E9E] mb-2" />
                  <p className="text-xs text-[#9E9E9E] font-medium">
                    {t("restaurantPromotionList").modal.image || "Add image"}
                  </p>
                  <p className="text-xs text-[#CCCCCC] mt-1">Click to upload</p>
                </>
              )}
            </div>
            {errors.image && (
              <p className="text-red-500 text-sm mt-1 text-center">
                {errors.image}
              </p>
            )}
          </div>

          <div className="flex gap-[30px] justify-end pt-[23px] border-t border-[#EEEEEE]">
            <button
              onClick={closeModal}
              className="text-white font-bold py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
            >
              {t("restaurantPromotionList").modal.cancel}
            </button>
            <button
              onClick={handleAddOffer}
              disabled={isAdding || isUpdating}
              className="rounded-[2px] bg-[#4CAF50] text-base text-white font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
            >
              {(isAdding || isUpdating) ? t("header").loading : isUpdateMode
                ? t("restaurantPromotionList").modal.update
                : t("restaurantPromotionList").modal.add}
            </button>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default PromotionList;
